import clsx from 'clsx';
import { type MouseEvent, type ReactNode } from 'react';
import { Icon } from '../Icon/Icon';

export type PillColor =
    | 'n0'
    | 'e50'
    | 'y20'
    | 'w50'
    | 'r50'
    | 'wm10'
    | 'wm30'
    | 'wm70'
    | 'wm90'
    | 'n50'
    | 'n20'
    | 'p90'
    | 'pp10'
    | 'pp30'
    | 'pp50';

export interface PillProps {
    id?: string;
    className?: string;
    children?: ReactNode | string;
    color?: PillColor;
    circle?: boolean;
    size?: 'xsmall' | 'small' | 'normal' | 'large';
    onDismiss?: (event: MouseEvent<HTMLButtonElement>) => void;
    dismissAriaLabel?: string;
}

export function Pill(props: PillProps): ReactNode {
    const pillClassName = clsx(
        'pill',
        props.color ? `pill-${props.color}` : null,
        `pill-size-${props.size ?? 'normal'}`,
        props.circle ? 'border-circle p-1' : null,
        props.onDismiss ? 'pill-dismissible' : null,
        props.className,
    );

    const transformedChildren =
        typeof props.children === 'string'
            ? props.children.toUpperCase()
            : props.children;

    function handleDismiss(event: MouseEvent<HTMLButtonElement>): void {
        event.preventDefault();
        event.stopPropagation();
        props.onDismiss?.(event);
    }

    return (
        <div id={props.id} className={pillClassName}>
            {transformedChildren}
            {props.onDismiss && (
                <button
                    type="button"
                    className="pill-dismiss"
                    aria-label={props.dismissAriaLabel ?? 'Remove'}
                    onClick={handleDismiss}
                >
                    <Icon name="cancel" />
                </button>
            )}
        </div>
    );
}
